/**
 * Job Page API Service
 * Connects to the Talenty backend to fetch AI-generated job page content
 */

import { getCompanyFromUrl } from '../utils/company';

// Job page content interfaces
export interface JobCompanyInfo {
  name: string;
  videoUrl?: string | null;
  textSummary: {
    blocks: Array<{
      title: string;
      content: string;
    }>;
  };
}

export interface JobHero {
  title: string;
  salary: string;
  location: string;
  experienceLevel: string;
  description: string;
  tags: string[];
  applicationUrl?: string;
}

export interface JobDescription {
  title: string;
  content: string;
  responsibilities?: string[];
  requirements?: string[];
}

export interface JobResponsibilities {
  title: string;
  tasks: Array<{
    id: string;
    title: string;
    description: string;
  }>;
}

export interface JobRequirements {
  title: string;
  requirements: Array<{
    id: string;
    title: string;
    description: string;
  }>;
}

export interface JobBenefits {
  title: string;
  subtitle?: string;
  benefits: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;
}

export interface JobApplicationProcess {
  title: string;
  subtitle?: string;
  steps: Array<{
    id: string;
    step: string;
    title: string;
    description: string;
  }>;
  contactInfo: {
    title: string;
    description: string;
    email: string;
    emailText: string;
  };
}

export interface JobTestimonials {
  title: string;
  testimonials: Array<{
    id: string;
    name: string;
    role: string;
    quote: string;
    image: string;
  }>;
}

export interface RelatedJob {
  id: string;
  slug: string;
  title: string;
  location: string;
  salary: string;
  experienceLevel: string;
  metaDescription: string;
  skills: string[];
}

export interface RelatedJobs {
  title: string;
  jobs: RelatedJob[];
}

// Complete job page content bundle interface
export interface JobPageContentBundle {
  questions?: Array<{ question: string; answer: string }>;
  // Job-specific information
  jobInfo: {
    id: string;
    slug: string;
    title: string;
    salary: string;
    canApply?: boolean;
    project?: { slug: string; title: string } | null;
    applicationBinding?: { placementId: string; placementSnapshotId: string; publicationOperationId: string } | null;
    location: string;
    experienceLevel: string;
    skills: string[];
    domainSlug: string;
    familySlug: string;
    metaDescription: string;
    keywords: string[];
  };
  
  // Content sections
  companyInfo: JobCompanyInfo;
  jobResponsibilities: JobResponsibilities;
  jobRequirements: JobRequirements;
  jobDescription: JobDescription;
  benefits: JobBenefits;
  applicationProcess: JobApplicationProcess;
  testimonials: JobTestimonials;
  relatedJobs: RelatedJobs;
  
  // Metadata
  generatedAt: string;
  mandantId?: string;
}

class JobPageApiService {
  private baseUrl: string;
  private companySlug: string;

  constructor() {
    // Configure API base URL via env with safe fallback to production backend
    this.baseUrl = import.meta.env.VITE_API_BASE_URL ||
      `https://ts.talenty.ai`;


    // Determine company from subdomain first, then path
    this.companySlug = getCompanyFromUrl();
  }


  /**
   * Get complete job page content for a specific job by slug
   */
  async getJobPageContent(jobSlug: string, signal?: AbortSignal): Promise<JobPageContentBundle> {
    // Use the catch-all endpoint to handle encoded slashes properly
    const url = `${this.baseUrl}/${encodeURIComponent(getCompanyFromUrl())}/career/jobs/page/${encodeURIComponent(jobSlug)}`;

    const response = await fetch(url, { signal });
    
    if (!response.ok) {
      throw new Error(`Die Stellenseite konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get job company information section
   */
  async getJobCompanyInfo(jobSlug: string): Promise<JobCompanyInfo> {
    const url = `${this.baseUrl}/${this.companySlug}/jobs/${encodeURIComponent(jobSlug)}/company-info`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Unternehmensinformationen konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get job description section
   */
  async getJobDescription(jobSlug: string): Promise<JobDescription> {
    const url = `${this.baseUrl}/${this.companySlug}/jobs/${encodeURIComponent(jobSlug)}/description`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Stellenbeschreibung konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get job benefits section
   */
  async getJobBenefits(jobSlug: string, mandant?: string): Promise<JobBenefits> {
    const params = new URLSearchParams();
    if (mandant) params.append('mandant', mandant);
    
    const url = `${this.baseUrl}/jobs/${encodeURIComponent(jobSlug)}/benefits${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Zusatzleistungen konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get job application process section
   */
  async getJobApplicationProcess(jobSlug: string, mandant?: string): Promise<JobApplicationProcess> {
    const params = new URLSearchParams();
    if (mandant) params.append('mandant', mandant);
    
    const url = `${this.baseUrl}/jobs/${encodeURIComponent(jobSlug)}/application-process${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Der Bewerbungsprozess konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get job testimonials section
   */
  async getJobTestimonials(jobSlug: string, mandant?: string): Promise<JobTestimonials> {
    const params = new URLSearchParams();
    if (mandant) params.append('mandant', mandant);
    
    const url = `${this.baseUrl}/jobs/${encodeURIComponent(jobSlug)}/testimonials${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Erfahrungsberichte konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get related jobs section
   */
  async getRelatedJobs(jobSlug: string, mandant?: string, limit: number = 3): Promise<RelatedJobs> {
    const params = new URLSearchParams();
    if (mandant) params.append('mandant', mandant);
    if (limit) params.append('limit', limit.toString());
    
    const url = `${this.baseUrl}/jobs/${encodeURIComponent(jobSlug)}/related${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Weitere passende Stellen konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Test API connectivity
   */
  async testConnection(): Promise<{ message: string; timestamp: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/jobs/test`);
    
    if (!response.ok) {
      throw new Error(`Der Dienst für Stellenseiten ist nicht erreichbar (Status ${response.status}).`);
    }
    
    return response.json();
  }
}

export const jobPageApi = new JobPageApiService();
